import apiClient from './api';

export type ServiceProviderCertificationStatus = 'reviewing' | 'approved' | 'rejected';
export type ServiceProviderAccountStatus = 'active' | 'inactive';
export type ServiceProviderAccountSearchStatus = ServiceProviderAccountStatus | 'all';
export type ServiceProviderCertificationSearchStatus = ServiceProviderCertificationStatus | 'all';
export type ServiceProviderSecurityDepositSearchStatus = 'paid' | 'unpaid' | 'all';

export interface ServiceProviderListParams {
  page?: number;
  pageSize?: number;
  name?: string;
  phone?: string;
  address?: string;
  serviceProject?: string;
  certificationStatus?: ServiceProviderCertificationSearchStatus;
  securityDepositPaid?: ServiceProviderSecurityDepositSearchStatus;
  accountStatus?: ServiceProviderAccountSearchStatus;
}

export interface ServiceProviderListItem {
  id: string;
  username: string;
  name: string;
  avatar: string;
  phone: string;
  wechatNickname: string;
  levelName: string;
  serviceRegion: string;
  serviceProjects: string[];
  serviceProjectsText: string;
  certificationStatus: ServiceProviderCertificationStatus;
  certificationRemark: string;
  certificationSubmittedAt: string;
  certificationReviewedAt: string;
  accountStatus: ServiceProviderAccountStatus | string;
  securityDepositAmount: number;
  securityDepositRefundableAmount: number;
  securityDepositPaid: boolean;
  withdrawableAmount: number;
  withdrawnAmount: number;
  distanceKm?: number | null;
  lastLoginAt: string;
  createdAt: string;
}

export interface ServiceProviderDetail {
  id: string;
  username: string;
  name: string;
  avatar: string;
  phone: string;
  appType: string;
  appId: string;
  openId: string;
  unionId: string;
  wechatNickname: string;
  wechatAvatarUrl: string;
  levelName: string;
  serviceProvince: string;
  serviceCity: string;
  serviceDistrict: string;
  serviceRegion: string;
  serviceLatitude: number;
  serviceLongitude: number;
  serviceAddressDetail: string;
  serviceExperience: string;
  serviceProjects: string[];
  serviceProjectsText: string;
  petTypes: string;
  specialAbilities: string;
  orderStartTime: string;
  orderEndTime: string;
  idCardFrontUrl: string;
  idCardBackUrl: string;
  extraMaterialUrl: string;
  bio: string;
  certificationStatus: ServiceProviderCertificationStatus;
  certificationRemark: string;
  certificationSubmittedAt: string;
  certificationReviewedAt: string;
  orderMode: boolean;
  autoAcceptOrder: boolean;
  notifyEnabled: boolean;
  accountStatus: ServiceProviderAccountStatus | string;
  securityDepositAmount: number;
  securityDepositRefundableAmount: number;
  securityDepositPaid: boolean;
  lastLoginAt: string;
  lastLoginIp: string;
  createdAt: string;
  updatedAt: string;
}

export interface ServiceProviderListResponse {
  list: ServiceProviderListItem[];
  total: number;
  page: number;
  pageSize: number;
  stats?: ServiceProviderListStats;
}

export interface ServiceProviderListStats {
  totalCount: number;
  certifiedCount: number;
  uncertifiedCount: number;
  depositPaidCount: number;
  depositUnpaidCount: number;
}

export interface UpdateServiceProviderRequest {
  name: string;
  phone?: string;
  levelName?: string;
  certificationStatus: ServiceProviderCertificationStatus;
  accountStatus: ServiceProviderAccountStatus;
  securityDepositPaid: boolean;
  serviceProvince?: string;
  serviceCity?: string;
  serviceDistrict?: string;
  serviceAddressDetail?: string;
  serviceExperience?: string;
  serviceProjects?: string;
  petTypes?: string;
  specialAbilities?: string;
  orderStartTime: string;
  orderEndTime: string;
  bio?: string;
  notifyEnabled: boolean;
  orderMode: boolean;
  autoAcceptOrder: boolean;
}

export interface DeductServiceProviderDepositRequest {
  amount: number;
  title?: string;
  remark?: string;
}

export const listServiceProviders = async (
  params?: ServiceProviderListParams,
): Promise<ServiceProviderListResponse> => {
  const response = await apiClient.get<ServiceProviderListResponse>('/service-providers', { params });
  return response.data;
};

export const getServiceProviderDetail = async (id: string): Promise<ServiceProviderDetail> => {
  const response = await apiClient.get<ServiceProviderDetail>(`/service-providers/${id}`);
  return response.data;
};

export const updateServiceProviderStatus = async (
  id: string,
  status: ServiceProviderAccountStatus,
): Promise<ServiceProviderDetail> => {
  const response = await apiClient.put<ServiceProviderDetail>(`/service-providers/${id}/status`, { status });
  return response.data;
};

export const updateServiceProvider = async (
  id: string,
  data: UpdateServiceProviderRequest,
): Promise<ServiceProviderDetail> => {
  const response = await apiClient.put<ServiceProviderDetail>(`/service-providers/${id}`, data);
  return response.data;
};

export const deductServiceProviderDeposit = async (
  id: string,
  data: DeductServiceProviderDepositRequest,
): Promise<ServiceProviderDetail> => {
  const response = await apiClient.post<ServiceProviderDetail>(`/service-providers/${id}/deposit-deductions`, data);
  return response.data;
};
